import React from 'react';
import { ArrowRight, Clock, Pencil, Trash2 } from 'lucide-react';
import { TimeEntry } from '../types/database';
import { cn } from '../lib/utils';
import { formatLongDate } from '../lib/format';
import { getShiftTone } from '../lib/shift-colors';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
import { ConfirmButton } from './ui/confirm-button';

interface ShiftDetailsProps {
  timeEntry: TimeEntry;
  selectedDate: Date;
  onEdit: () => void;
  onDelete?: (id: number) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
}

export const ShiftDetails: React.FC<ShiftDetailsProps> = ({
  timeEntry,
  selectedDate,
  onEdit,
  onDelete,
  isOpen,
  onClose
}) => {
  const handleDelete = async () => {
    if (timeEntry.id && onDelete) {
      try {
        await onDelete(timeEntry.id);
        onClose();
      } catch (error) {
        console.error('Failed to delete time entry:', error);
      }
    }
  };

  const shifts = timeEntry.shifts ?? [];

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      icon={<Clock />}
      title="Shifts"
      description={formatLongDate(selectedDate)}
      footer={
        <>
          {onDelete && (
            <ConfirmButton onConfirm={handleDelete} confirmLabel="Confirm delete">
              <Trash2 />
              Delete
            </ConfirmButton>
          )}
          <Button variant="secondary" className="ml-auto" onClick={onClose}>
            Close
          </Button>
          <Button onClick={onEdit}>
            <Pencil />
            Edit
          </Button>
        </>
      }
    >
      <ol className="flex flex-col gap-2">
        {shifts.map((shift, index) => {
          const tone = getShiftTone(index);
          return (
            <li
              key={index}
              className={cn(
                'flex items-center gap-3 rounded-xl border border-l-[3px] border-line bg-raised/60 px-4 py-3',
                tone.panel
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs text-fg-subtle">Shift {index + 1}</p>
                <p className="numeric mt-0.5 flex items-center gap-2 text-[15px] text-fg">
                  {shift.clockIn || '--:--'}
                  <ArrowRight className="size-3.5 text-fg-subtle" />
                  {shift.clockOut || <span className="text-success">in progress</span>}
                </p>
              </div>
              <span className="numeric rounded-md bg-white/[0.05] px-2 py-1 text-[13px] text-fg-muted">
                {shift.hours?.toFixed(2) || '0.00'}h
              </span>
            </li>
          );
        })}
      </ol>

      <div className="mt-3 flex items-center justify-between rounded-xl bg-white/[0.03] px-4 py-3">
        <span className="text-[13px] text-fg-muted">Total for the day</span>
        <span className="numeric text-lg font-medium text-fg">{timeEntry.totalHours?.toFixed(2) || '0.00'}h</span>
      </div>
    </Dialog>
  );
};
